import React from 'react';
import { downloadUserDoc } from '@services/docs';
import { DOCUMENT_DOWNLOAD_KINDS, saveBlobAsFile } from '@utils/documentVault';

export const DOWNLOAD_STATUS = {
  INACTIVE: 'inactive',
  RUNNING: 'running',
  COMPLETED: 'completed',
  ERROR: 'error',
};

/*
  A clean copy re-runs the whole watermark-stripping pipeline server-side behind a 180s read
  timeout, so a slow download is normal rather than broken. The elapsed counter exists to say so
  to the user, and starts reporting only once the wait stops feeling instant.
*/
const ELAPSED_HINT_DELAY_MS = 10000;
const ELAPSED_TICK_MS = 1000;

export function useDocumentDownload(doc) {
  const [state, setState] = React.useState({ status: DOWNLOAD_STATUS.INACTIVE, kind: null, error: null });
  const [elapsedMs, setElapsedMs] = React.useState(0);
  const abortRef = React.useRef(null);
  const isMountedRef = React.useRef(true);

  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Leaving the screen mid-download abandons the request: the backend hands back no job id,
      // so there is nothing to reattach to on return.
      abortRef.current?.abort();
    };
  }, []);

  const isRunning = state.status === DOWNLOAD_STATUS.RUNNING;

  React.useEffect(() => {
    if (!isRunning) {
      setElapsedMs(0);
      return undefined;
    }

    const startedAt = Date.now();
    let interval = null;

    // Nothing displays the counter until the hint appears, so don't re-render once a second
    // for the entire wait -- most downloads finish well inside the delay.
    const timeout = setTimeout(() => {
      setElapsedMs(Date.now() - startedAt);
      interval = setInterval(() => {
        setElapsedMs(Date.now() - startedAt);
      }, ELAPSED_TICK_MS);
    }, ELAPSED_HINT_DELAY_MS);

    return () => {
      clearTimeout(timeout);
      if (interval) clearInterval(interval);
    };
  }, [isRunning]);

  const download = React.useCallback(
    async (kind) => {
      // The clean endpoint explicitly must not receive concurrent requests for the same blob,
      // and a second click on either kind would only race the first to the same file.
      if (abortRef.current) return null;

      const controller = new AbortController();
      abortRef.current = controller;
      setState({ status: DOWNLOAD_STATUS.RUNNING, kind, error: null });

      const result = await downloadUserDoc(doc?.blob_id, {
        clean: kind === DOCUMENT_DOWNLOAD_KINDS.clean,
        filename: doc?.filename,
        signal: controller.signal,
      });

      abortRef.current = null;
      if (!isMountedRef.current) return result;

      if (result.canceled) {
        setState({ status: DOWNLOAD_STATUS.INACTIVE, kind: null, error: null });
        return result;
      }

      if (!result.ok) {
        setState({ status: DOWNLOAD_STATUS.ERROR, kind, error: result.error, statusCode: result.status });
        return result;
      }

      saveBlobAsFile(result.blob, result.filename);
      setState({
        status: DOWNLOAD_STATUS.COMPLETED,
        kind,
        error: null,
        // A clean-copy request for a non-PDF returns the untouched original with a 200. The user
        // asked for a watermark-free copy and got the same file, so say so rather than implying
        // a clean pass happened.
        wasPassthrough: kind === DOCUMENT_DOWNLOAD_KINDS.clean && !result.wasCleaned,
      });
      return result;
    },
    [doc?.blob_id, doc?.filename]
  );

  const downloadOriginal = React.useCallback(() => download(DOCUMENT_DOWNLOAD_KINDS.original), [download]);
  const downloadCleanCopy = React.useCallback(() => download(DOCUMENT_DOWNLOAD_KINDS.clean), [download]);

  const reset = React.useCallback(() => {
    setState({ status: DOWNLOAD_STATUS.INACTIVE, kind: null, error: null });
  }, []);

  return {
    status: state.status,
    kind: state.kind,
    error: state.error,
    statusCode: state.statusCode,
    wasPassthrough: state.wasPassthrough,
    isRunning,
    showElapsedHint: isRunning && elapsedMs >= ELAPSED_HINT_DELAY_MS,
    elapsedSeconds: Math.floor(elapsedMs / 1000),
    downloadOriginal,
    downloadCleanCopy,
    reset,
  };
}
